import { useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useIsMounted, useOverlayContext } from 'seagull';
import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import { useIoContext, useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { MAX_FILE_SIZE_BYTES } from '../ImportAgreementModal.utils';
const KNOWN_ERROR_REASONS = [
    'insufficient_data',
    'not_an_agreement'
];
export const isKnownErrorReason = (reason)=>reason != null && KNOWN_ERROR_REASONS.includes(reason);
export const useImportAgreement = (testIds, onSuccess)=>{
    const [modalState, setModalState] = useState('upload');
    const [isUploading, setIsUploading] = useState(false);
    const [fileName, setFileName] = useState(null);
    const [errorReason, setErrorReason] = useState(null);
    const [conversationId, setConversationId] = useState(null);
    const [extractionSuccess, setExtractionSuccess] = useState(false);
    const extractionRef = useRef(null);
    const successConversationIdRef = useRef(null);
    const { api } = useStoreActions();
    const { browser: { uploadFileToCloudStorage } } = useIoContext();
    const { showErrorToast } = useOverlayContext();
    const { t } = useLocaleKeys();
    const isMounted = useIsMounted();
    const resetToUpload = ()=>{
        setModalState('upload');
        setErrorReason(null);
        setConversationId(null);
        setExtractionSuccess(false);
        setFileName(null);
    };
    const showGenericErrorAndReset = ()=>{
        showErrorToast(t.agreementImport.errorToast.generic(), testIds.GenericErrorToast());
        resetToUpload();
    };
    const processFile = async (file)=>{
        if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
            showErrorToast(t.agreementImport.uploadModal.fileTypeError(), testIds.FileTypeErrorToast());
            return;
        }
        if (file.size > MAX_FILE_SIZE_BYTES) {
            showErrorToast(t.agreementImport.uploadModal.fileSizeError(), testIds.FileSizeErrorToast());
            return;
        }
        setIsUploading(true);
        setFileName(file.name);
        setModalState('analyzing');
        try {
            const id = generateUniqueId('attachment');
            const filesData = [
                {
                    id,
                    displayName: file.name,
                    size: file.size
                }
            ];
            const { urls } = await api.business.getUploadUrls(filesData);
            const uploadInfo = urls[0];
            await uploadFileToCloudStorage(uploadInfo.url, file, uploadInfo.headers);
            setIsUploading(false);
            try {
                const response = await api.agreementImport.extract({
                    fileId: id
                });
                if (!isMounted()) return;
                if (response.success) {
                    if (response.conversationId == null) {
                        showGenericErrorAndReset();
                        return;
                    }
                    extractionRef.current = response.extraction;
                    successConversationIdRef.current = response.conversationId;
                    setConversationId(response.conversationId);
                    setExtractionSuccess(true);
                } else if (isKnownErrorReason(response.reason)) {
                    setErrorReason(response.reason);
                    if (response.conversationId) {
                        setConversationId(response.conversationId);
                    }
                    setModalState('error');
                } else {
                    showGenericErrorAndReset();
                }
            } catch (unused) {
                if (!isMounted()) return;
                showGenericErrorAndReset();
            }
        } catch (error) {
            Sentry.captureException(error);
            showErrorToast(t.agreementImport.uploadModal.uploadError(), testIds.UploadErrorToast());
            setIsUploading(false);
            setModalState('upload');
        }
    };
    const submitFeedback = (rating)=>{
        if (conversationId == null) return;
        api.agreementImport.feedback({
            conversationId,
            rating
        }).catch(()=>{});
    };
    const notifySuccess = ()=>{
        if (!isMounted()) return;
        if (extractionRef.current == null || successConversationIdRef.current == null) return;
        onSuccess(extractionRef.current, successConversationIdRef.current);
    };
    return {
        modalState,
        isUploading,
        fileName,
        errorReason,
        conversationId,
        extractionSuccess,
        processFile,
        submitFeedback,
        resetToUpload,
        notifySuccess
    };
};
